import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  BadgePercent,
  Banknote,
  Building2,
  Bell,
  ScanFace,
  Share2,
  PhoneCall,
  ListOrdered,
  Headphones,
  MessageCircle,
  Gift,
  Bot,
  FileCheck2,
  ShieldCheck,
  TrendingUp,
  ShieldAlert,
  Wallet,
  CheckCircle2,
  IdCard,
  EyeOff,
  Images,
  Languages,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedSection, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO, { SITE_URL, breadcrumbs } from "@/components/SEO";

const PATH = "/industries/fintech";
const IMG = "/industries-images/fintech";

const heroStats = [
  { val: "18", label: "AI offerings across the lending lifecycle" },
  { val: "99.9%", label: "Document processing accuracy, on-premise" },
  { val: "99.7%", label: "Face verification accuracy" },
  { val: "85%", label: "Less manual data entry with document AI" },
];

const levers = [
  {
    icon: TrendingUp,
    title: "Approval rate",
    body: "Thin-file salaried borrowers get declined on bureau alone while the bank statements and payslips that would approve them go unread.",
  },
  {
    icon: ShieldAlert,
    title: "Credit loss",
    body: "Edited PDFs, synthetic identities and mule rings pass KYC and OCR. The loss surfaces months later as a first-payment default.",
  },
  {
    icon: Wallet,
    title: "Cost to collect",
    body: "Bucket 0 to 30 is worked by dialler teams. Cost rises with the book, and tone on recorded calls is a conduct risk under RBI rules.",
  },
];

const stages = [
  {
    id: "underwriting",
    label: "Underwriting and risk",
    kicker: "Offerings 01 to 04",
    title: "Approve more of the right borrowers, and see the slip before it happens",
    intro:
      "The biggest prize in a personal-loan or SME book is the thin-file borrower you decline today and the stressed account you see too late.",
    image: `${IMG}/underwriting.jpg`,
    offerings: [
      {
        num: "01",
        icon: BadgePercent,
        title: "Alternative-data credit model",
        problem: "Bureau-only scorecards decline thin-file borrowers who would repay.",
        build:
          "Gradient-boosted and graph models on bank flows, UPI behaviour, employer stability, device signals and bureau data, trained and validated on your own disbursed and rejected book. Served as an API to your LOS with reason codes on every decision.",
        outcomes: ["Higher approvals at the same loss", "Cash-flow scoring for thin files", "Reason codes for credit and RBI"],
      },
      {
        num: "02",
        icon: Banknote,
        title: "Bank statement and payslip intelligence",
        problem: "Statements arrive from dozens of banks in dozens of layouts, and analysts miss edited files.",
        build:
          "LLM plus OCR pipeline that parses any Indian bank statement into clean transactions, with tamper and synthetic-statement detection at pixel and transaction level. Salary regularity, EMI load, bounce history and lender exposure from uploaded PDFs or Account Aggregator feeds.",
        outcomes: ["Minutes per statement, any bank", "Tampered PDFs flagged before credit", "One pipeline for PDF and AA data"],
      },
      {
        num: "03",
        icon: Building2,
        title: "Employer risk engine with EPFO (PF) verification",
        problem: "A salaried loan is really an employer loan, yet whether the employer is real, and still paying PF for this borrower, goes unchecked.",
        build:
          "Verifies that the declared employer exists and is active, and that the applicant appears in its EPFO (PF) contributions. On top of that, a continuous employer health score from MCA filings, EPFO contribution data, hiring and layoff news and salary-delay patterns in your own book. Watchlists and category caps that update themselves, used in underwriting and portfolio monitoring.",
        outcomes: ["Employer and employment verified from PF data", "Live employer score with early warning", "Exposure by employer and sector"],
      },
      {
        num: "04",
        icon: Bell,
        title: "Early warning system",
        problem: "The first signal of stress is a bounced EMI, which is already a DPD.",
        build:
          "A model that predicts the probability of a missed EMI 30 days ahead from repayment history, bank behaviour, app engagement and employer score. Daily risk tiers pushed into the LMS and collections queue, with pre-emptive nudges and restructure offers before the due date.",
        outcomes: ["30-day head start on slips", "Pre-emptive outreach", "Lower roll-forward to bucket 30"],
      },
    ],
  },
  {
    id: "fraud",
    label: "Fraud and KYC",
    kicker: "Offerings 05 to 09",
    title: "Catch the fraud that passes KYC",
    intro:
      "Deepfakes, reused photos, synthetic identities and mule rings are now the main source of first-payment default in digital lending. Five offerings stop them before disbursal and keep Aadhaar data compliant.",
    image: `${IMG}/fraud.jpg`,
    offerings: [
      {
        num: "05",
        icon: IdCard,
        title: "Aadhaar and PAN detection in milliseconds",
        problem: "Every upload has to be sorted and checked for Aadhaar and PAN by hand, which slows onboarding and lets mismatches through.",
        build:
          "Vision models that find and classify Aadhaar and PAN cards inside any photo, scan or multi-page PDF, read the fields, validate format and checksum, and cross-check them against the applicant's details. Responds in milliseconds, so it runs inline in the onboarding flow.",
        outcomes: ["Millisecond response, inline in onboarding", "Fields extracted and validated", "Mismatches flagged before KYC"],
      },
      {
        num: "06",
        icon: EyeOff,
        title: "Aadhaar masking across scanned loan files",
        problem: "Loan against property and secured loan files carry full Aadhaar numbers across hundreds of scanned pages, and UIDAI rules require them masked.",
        build:
          "Finds every Aadhaar number, card image and QR code across scanned agreements, property papers and KYC packs, and masks the first eight digits before the file is stored or shared. Works on the existing back-book in bulk as well as on new files, with a page-level audit log.",
        outcomes: ["UIDAI-compliant masking at scale", "Back-book cleaned in bulk", "Page-level audit log"],
      },
      {
        num: "07",
        icon: ScanFace,
        title: "Deepfake selfie and document detection",
        problem: "Selfies, video KYC and uploaded documents are being beaten by face-swaps, replay attacks and AI-generated or edited files.",
        build:
          "Liveness detection, face match and injection-attack detection trained on Indian ID formats and lighting conditions, plus detection of AI-generated and edited documents at pixel and layout level. Runs on-premise or in your VPC so biometric data never leaves your control.",
        outcomes: ["99.7% face verification accuracy", "Deepfake selfies and forged documents caught", "Biometric data stays with you"],
      },
      {
        num: "08",
        icon: Images,
        title: "Fraud photo match across applications",
        problem: "The same face or the same document photo reappears under different names, and nothing compares it across applications.",
        build:
          "Face and image embeddings for every selfie and document photo, searched against your full application history in milliseconds. Flags the same person under a new identity, reused document images and known-fraud faces, with the matched applications shown side by side.",
        outcomes: ["Repeat fraudsters caught on reapply", "Search across your whole book", "Matches shown side by side"],
      },
      {
        num: "09",
        icon: Share2,
        title: "Synthetic identity, mule and transaction fraud detection",
        problem: "Fraud rings apply as dozens of clean-looking individuals, and anomalies are flagged after the money has moved.",
        build:
          "Link analysis across phone, device, email, address, bank account and employer to find clusters. Graph features and millisecond-scale velocity rules scored at application, payout and across live transaction networks, with an analyst console that shows the linked entities behind every flag.",
        outcomes: ["Rings caught at the second application", "Payout accounts screened for mule patterns", "Millisecond anomaly flagging"],
      },
    ],
  },
  {
    id: "collections",
    label: "Collections",
    kicker: "Offerings 10 to 12",
    title: "The fastest return in the stack is collections",
    intro:
      "Collections shows a measurable number within 60 days without touching your credit policy. Three offerings that cut cost to collect and conduct risk together.",
    image: `${IMG}/collections.jpg`,
    offerings: [
      {
        num: "10",
        icon: PhoneCall,
        title: "Voice AI collection agents",
        problem: "Bucket 0 to 30 reminders are worked by human dialler teams, and coverage drops at month end.",
        build:
          "Voice agents in Hindi, Kannada, Tamil, Telugu, Hinglish and English that remind, capture promise-to-pay and offer settlements within approved bands. Guardrails for calling hours, language and escalation are built in, with hand-off to a human the moment the borrower asks or disputes.",
        outcomes: ["100% of bucket 0 to 30 reached", "Fraction of dialler cost per contact", "Compliant by design"],
      },
      {
        num: "11",
        icon: ListOrdered,
        title: "Collections prioritisation model",
        problem: "Agents call lists in order of amount or DPD, not likelihood to pay.",
        build:
          "A model that ranks each delinquent account by propensity to pay and recoverable amount, then recommends channel (voice AI, WhatsApp, human, field), best time and settlement offer. Feeds the dialler and the voice agents directly.",
        outcomes: ["Right call, right borrower, right time", "5 to 15% typical lift in roll-back", "Higher recovery per agent hour"],
      },
      {
        num: "12",
        icon: Headphones,
        title: "Agent call QA",
        problem: "QA listens to a small sample of recovery calls by ear, and harassment-risk language is found only after a complaint.",
        build:
          "Transcription of every call in Indian languages, compliance scoring against RBI recovery guidelines and your script, and daily flags, agent scorecards and coaching clips for team leads.",
        outcomes: ["100% of calls scored", "Same-day flag on conduct risk", "Evidence for RBI and ombudsman"],
      },
    ],
  },
  {
    id: "customer",
    label: "Customer-facing",
    kicker: "Offerings 13 to 14",
    title: "Serve borrowers in their language, at scale",
    intro: "Two offerings that lift conversion and cut support cost inside the app and on WhatsApp.",
    image: `${IMG}/customer.jpg`,
    offerings: [
      {
        num: "13",
        icon: MessageCircle,
        title: "Vernacular loan assistant",
        problem: "Eligibility, document and EMI questions flood support in a dozen languages, and drop-off happens at the document step.",
        build:
          "An assistant in the app and on WhatsApp that answers in the borrower's language, with tool-calling into your LOS and LMS for eligibility, document status, EMI, foreclosure and statements. Escalates to a human with the full context attached.",
        outcomes: ["24x7 support in Indian languages", "Fewer drop-offs at the document step", "Lower cost per resolved query"],
      },
      {
        num: "14",
        icon: Gift,
        title: "Personalised offer engine",
        problem: "Top-up and pre-approved limits run as monthly batch campaigns that ignore current cash-flow and risk.",
        build:
          "Real-time decisions on limit, top-up, rate and tenure for each borrower, using repayment behaviour, bank flows and the early-warning score. Delivered in-app, on WhatsApp and through the assistant.",
        outcomes: ["Real-time offers, not monthly batches", "Higher top-up take-up", "Risk-priced limits"],
      },
    ],
  },
  {
    id: "operations",
    label: "Operations and compliance",
    kicker: "Offerings 15 to 18",
    title: "Credit ops and compliance, with people approving only exceptions",
    intro: "Four offerings that turn documents in any language into decisions, filings and audit trails.",
    image: `${IMG}/operations.jpg`,
    offerings: [
      {
        num: "15",
        icon: Languages,
        title: "Multilingual OCR for any document",
        problem: "Payslips, property papers, ITRs and handwritten forms arrive in English, Hindi and regional scripts, and generic OCR fails on them.",
        build:
          "Deep-learning OCR that reads printed and handwritten text in English and major Indian scripts across scans, phone photos and PDFs, and returns structured fields with confidence scores. It feeds every other offering on this page, from statement parsing to Aadhaar masking.",
        outcomes: ["English and Indian scripts", "Handwriting and phone photos", "Structured fields with confidence scores"],
      },
      {
        num: "16",
        icon: Bot,
        title: "Agentic credit ops copilot",
        problem: "A credit analyst opens bureau, statement, payslip and KYC in four tabs and types the memo by hand.",
        build:
          "One agent that pulls bureau, statement analysis, employer score, fraud flags and KYC into a one-page credit memo with a recommended decision checked against your policy. The analyst edits and approves, and every step is logged.",
        outcomes: ["One-page memo in seconds", "Consistent decisions across shifts", "Faster manual-review turnaround"],
      },
      {
        num: "17",
        icon: FileCheck2,
        title: "Regulatory reporting and finance document automation",
        problem: "RBI returns and co-lending reports are compiled by hand, and invoices, expense claims and contracts are keyed in or scanned manually.",
        build:
          "Automated RBI returns and co-lending reconciliation with partner banks. Document AI for invoice reconciliation, expense management and vendor bills: extract, validate and post to the ERP. Clause-level regulatory scanning of contracts and policies.",
        outcomes: ["85% less manual data entry", "Reconciled co-lending books every cycle", "Audit trail from filing to source"],
      },
      {
        num: "18",
        icon: ShieldCheck,
        title: "Model risk and explainability layer",
        problem: "RBI's digital lending guidelines expect explainable, fair decisions, and model drift goes unnoticed between annual validations.",
        build:
          "Reason codes on every score with SHAP-style attributions in plain language, drift, stability and fairness monitoring on live decisions, and a model inventory with validation reports and an audit console.",
        outcomes: ["Every decline explained", "Continuous monitoring", "Ready for RBI, auditors and the board"],
      },
    ],
  },
];

const roadmap = [
  { when: "Weeks 1 to 8", title: "Prove the number", body: "Voice AI collections on bucket 0 to 30 plus the early warning model, measured against a control group.", nums: ["10", "04"] },
  { when: "Months 3 to 4", title: "Stop the fraud", body: "Aadhaar and PAN detection, deepfake selfie and document checks, fraud photo match and mule detection on live applications.", nums: ["05", "07", "08", "09"] },
  { when: "Months 5 to 6", title: "Lift approvals", body: "Bank statement intelligence, the alternative-data credit model, PF-based employer verification and the credit ops copilot, validated on your book.", nums: ["02", "01", "03", "16"] },
  { when: "Month 7 onwards", title: "Scale and govern", body: "Aadhaar masking, multilingual OCR, prioritisation, call QA, vernacular assistant, offer engine, regulatory reporting and the explainability layer.", nums: ["06", "15", "11", "12", "13", "14", "17", "18"] },
];

const caseStudies = [
  {
    id: "onprem-document-ai",
    title: "Sovereign Document AI & Fraud Detection API",
    client: "Leading National Retail Bank",
    description: "Fully on-premise document processing API with pixel-level tampering checks, KYC validation and deep-learning OCR.",
    metric: "99.9% document processing accuracy",
  },
  {
    id: "compliance-qa-platform",
    title: "Conversational QA & Compliance Audit Platform",
    client: "Global Wealth Management Firm",
    description: "Secure compliance assistant answering questions over regulations and policies with full citation attribution.",
    metric: "90% faster compliance auditing",
  },
  {
    id: "ai-invoicing-engine",
    title: "Cognitive Document AI for Financial Decision-Making",
    client: "Global Enterprise",
    description: "Multi-format, multi-language invoice intake, extraction, validation and ERP posting at enterprise volume.",
    metric: "Invoice backlog eliminated",
  },
];

const faqs = [
  {
    q: "Which fintech and lending companies is this built for?",
    a: "Digital lenders and NBFCs offering personal, SME, gold and vehicle loans, co-lending platforms, payment companies and wealth platforms in India. The offerings are modular, so a lender can start with collections or fraud and add underwriting later.",
  },
  {
    q: "Does borrower, bureau or biometric data leave our environment?",
    a: "No. Every offering runs on-premise or inside your VPC. Models are trained on your data in your environment and you own the code, the models and the data.",
  },
  {
    q: "How do you handle RBI digital lending and fair practice requirements?",
    a: "Every credit decision carries reason codes and a source trail, voice agents operate inside calling-hour, language and escalation guardrails, every recovery call is transcribed and scored, and the explainability layer monitors drift and fairness continuously.",
  },
  {
    q: "What does a first engagement look like?",
    a: "Two weeks of discovery on real applications, statements and calls, then the highest-value offering live on a pilot segment in about eight weeks, measured against a control group. Pricing is fixed and milestone-based, with two months of support after go-live.",
  },
  {
    q: "Which systems do you integrate with?",
    a: "Loan origination and management systems, bureaus, Account Aggregator frameworks, diallers and telephony, WhatsApp Business, core banking and ERP, through APIs and secure data exchange.",
  },
];

const description =
  "Deep tech AI for digital lending and fintech: alternative-data credit models, bank statement intelligence, Aadhaar and PAN detection and masking, deepfake selfie and document detection, fraud photo match, mule detection, multilingual OCR, voice AI collections, vernacular assistants and RBI-ready compliance automation, built and deployed by VANCO.";

export default function FintechPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI for Fintech and Digital Lending",
      serviceType: "AI solutions for fintech, NBFCs and digital lenders",
      description,
      url: `${SITE_URL}${PATH}`,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: [{ "@type": "Country", name: "India" }, "Worldwide"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Fintech AI offerings",
        itemListElement: stages.flatMap((s) =>
          s.offerings.map((o) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: o.title } }))
        ),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    breadcrumbs([
      { name: "Home", path: "/" },
      { name: "Fintech AI Solutions", path: PATH },
    ]),
  ];

  return (
    <div className="overflow-hidden bg-white text-slate-800" data-testid="industry-page" id="industry-page-fintech">
      <SEO title="AI for Fintech and Digital Lending | VANCO" description={description} path={PATH} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 bg-slate-950 text-white overflow-hidden" data-testid="industry-hero">
        <img
          src={`${IMG}/hero.jpg`}
          alt="Bengaluru skyline at dusk"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/60" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/15 text-[#f7c550] rounded-full text-xs font-bold tracking-widest uppercase mb-6 font-['Inter']">
              Fintech and digital lending
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope'] mb-6 leading-tight text-white">
              Deep tech AI for every step of the loan, from first tap to last EMI
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-10 font-['Inter']">
              Eighteen production-grade AI offerings for lenders, NBFCs and fintechs across underwriting, fraud, collections, customer service and RBI compliance. Built on your data, deployed in your environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="rounded-full px-8 py-6 text-base font-bold bg-[#f7c550] hover:bg-[#f5b82e] text-slate-950 shadow-lg shadow-amber-500/20">
                  Talk to an expert
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#offerings">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-base font-semibold border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white">
                  See the 18 offerings
                  <ArrowDown className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {heroStats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5">
                <div className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#f7c550]">{s.val}</div>
                <div className="text-sm text-slate-200 mt-1 font-['Inter']">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three levers */}
      <AnimatedSection className="py-24 bg-white" data-testid="fintech-levers">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Why now</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-5">
              A lending book runs on three numbers. Most of what moves them is still done by hand.
            </h2>
            <p className="text-lg text-slate-600 font-['Inter']">
              People reading statements, calling borrowers and compiling returns. We build the AI that does the reading, scoring, calling and explaining, inside the LOS, LMS and dialler you already run.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-3 gap-6">
            {levers.map((l) => {
              const Icon = l.icon;
              return (
                <AnimatedItem key={l.title}>
                  <div className="h-full rounded-3xl border border-slate-200/70 bg-slate-50/60 p-8 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0f172a] font-['Manrope'] mb-3">{l.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed font-['Inter']">{l.body}</p>
                  </div>
                </AnimatedItem>
              );
            })}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Stage navigation */}
      <section id="offerings" className="bg-[#243b7f] text-white py-16 scroll-mt-20" data-testid="fintech-offerings-nav">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">The offerings</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope']">
                Eighteen AI systems, organised by where they hit the P&amp;L
              </h2>
            </div>
            <div className="lg:col-span-5">
              <ul className="flex flex-wrap gap-2">
                {stages.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="inline-block px-4 py-2 rounded-full border border-white/20 bg-white/5 hover:bg-[#f7c550] hover:text-slate-950 hover:border-[#f7c550] text-sm font-semibold transition-colors font-['Inter']"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stages */}
      {stages.map((stage, si) => (
        <section
          key={stage.id}
          id={stage.id}
          className={`py-24 scroll-mt-20 ${si % 2 === 0 ? "bg-white" : "bg-slate-50/70"} border-t border-slate-100`}
          data-testid={`fintech-stage-${stage.id}`}
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-4 lg:sticky lg:top-28">
                <div className="relative rounded-3xl overflow-hidden aspect-[3/2] mb-7 shadow-xl shadow-slate-200/70">
                  <img src={stage.image} alt={stage.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                  <span className="absolute left-5 bottom-5 inline-block px-3 py-1.5 bg-[#f7c550] text-slate-950 rounded-md text-xs font-bold uppercase tracking-wider font-['Inter']">
                    {stage.kicker}
                  </span>
                </div>
                <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-3 font-['Inter']">{stage.label}</p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-4">{stage.title}</h2>
                <p className="text-slate-600 font-['Inter'] leading-relaxed">{stage.intro}</p>
              </div>

              <div className="lg:col-span-8 space-y-6">
                {stage.offerings.map((o) => {
                  const Icon = o.icon;
                  return (
                    <article
                      key={o.num}
                      className="group rounded-3xl bg-white border border-slate-200/70 p-7 md:p-9 shadow-sm hover:shadow-2xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-500"
                      data-testid={`fintech-offering-${o.num}`}
                    >
                      <div className="flex items-start gap-5">
                        <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center">
                          <Icon className="w-7 h-7" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs font-bold tracking-widest text-[#243b7f] font-['Inter']">{o.num}</span>
                            <span className="h-px flex-1 bg-slate-200" />
                          </div>
                          <h3 className="text-xl md:text-2xl font-bold text-[#0f172a] font-['Manrope'] mb-3 group-hover:text-[#243b7f] transition-colors">
                            {o.title}
                          </h3>
                          <p className="text-sm font-semibold text-red-600/90 mb-4 font-['Inter']">
                            <span className="uppercase tracking-wider text-[11px] mr-2">The problem</span>
                            <span className="text-slate-700 font-normal">{o.problem}</span>
                          </p>
                          <p className="text-slate-600 text-sm md:text-[15px] leading-relaxed font-['Inter'] mb-5">
                            <span className="uppercase tracking-wider text-[11px] font-bold text-[#243b7f] mr-2">What we build</span>
                            {o.build}
                          </p>
                          <ul className="flex flex-wrap gap-2">
                            {o.outcomes.map((t) => (
                              <li key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold font-['Inter'] border border-emerald-100">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Roadmap */}
      <AnimatedSection className="py-24 bg-[#0f172a] text-white relative overflow-hidden" data-testid="fintech-roadmap">
        <img src={`${IMG}/bengaluru.jpg`} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/80 via-[#0f172a]/90 to-[#0f172a]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">Where to start</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope'] mb-5">
              Start where the number shows fastest, then move up the funnel
            </h2>
            <p className="text-slate-300 text-lg font-['Inter']">
              Collections shows a measurable result within 60 days, does not touch your credit policy, and builds the trust that underwriting work needs. The bigger prize comes once that trust exists.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((r, i) => (
              <AnimatedItem key={r.title}>
                <div className={`h-full rounded-3xl p-7 border ${i === 0 ? "bg-[#f7c550] text-slate-950 border-[#f7c550]" : "bg-white/5 border-white/10"}`}>
                  <div className={`text-xs font-bold tracking-widest uppercase mb-3 font-['Inter'] ${i === 0 ? "text-slate-800" : "text-[#f7c550]"}`}>{r.when}</div>
                  <h3 className="text-xl font-bold font-['Manrope'] mb-3">{r.title}</h3>
                  <p className={`text-sm leading-relaxed font-['Inter'] mb-5 ${i === 0 ? "text-slate-800" : "text-slate-300"}`}>{r.body}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.nums.map((n) => (
                      <span key={n} className={`px-2 py-1 rounded-md text-[11px] font-bold font-['Inter'] ${i === 0 ? "bg-slate-950/10 text-slate-900" : "bg-white/10 text-white"}`}>{n}</span>
                    ))}
                  </div>
                </div>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Success stories */}
      <section className="py-24 bg-slate-50/50 border-t border-b border-slate-100" data-testid="industry-case-studies">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Delivered work</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope']">
              Proven in production for banks, wealth managers and enterprises
            </h2>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <AnimatedItem key={study.id}>
                <Link to={`/case-studies/${study.id}`} className="group block h-full">
                  <div className="h-full flex flex-col justify-between min-h-[320px] bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:shadow-slate-200/50 hover:-translate-y-2 transition-all duration-500">
                    <div>
                      <div className="inline-block px-3 py-1.5 bg-[#f7c550] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded-md mb-6">Case Study</div>
                      <h3 className="text-[#0a122c] text-xl font-bold font-['Manrope'] mb-2 leading-snug group-hover:text-[#243b7f] transition-colors">{study.title}</h3>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 font-['Inter']">{study.client}</p>
                      <p className="text-slate-600 text-sm leading-relaxed font-['Inter'] mb-4">{study.description}</p>
                      <p className="text-[#243b7f] text-sm font-bold font-['Inter']">{study.metric}</p>
                    </div>
                    <span className="text-[#243b7f] font-semibold text-sm hover:underline inline-flex items-center gap-1 mt-6">
                      Read Now
                      <ArrowRight className="w-4 h-4 ml-0.5" />
                    </span>
                  </div>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="fintech-faq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-10">Questions lenders ask us</h2>
          <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
            {faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none text-lg font-semibold text-[#0f172a] font-['Manrope']">
                  {f.q}
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed font-['Inter'] pr-12">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#243b7f]" data-testid="industry-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Manrope'] mb-6">
            Let's put the first number on the board
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto font-['Inter']">
            A 45-minute working session: bank statement intelligence and the voice collections agent, run live on sample data. Then we pick the 60-day pilot and agree how the number gets measured.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105">
              Book the working session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
